import React, { useMemo } from 'react';
import { BarChart3, Users, Briefcase, Info, Sparkles, Download, Layers, DollarSign, TrendingUp } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import DashboardCards from './DashboardCards';
import ExecutiveSummary from './ExecutiveSummary';
import LiveIndicator from '../ui/LiveIndicator';
import PersonaChart from './PersonaChart';
import MeetingRevenueChart from './MeetingRevenueChart';
import RevenueWorkloadChart from './RevenueWorkloadChart';
import MessageVolumeChart from './MessageVolumeChart';
import CareWatchlistTile from './CareWatchlistTile';
import ExportButton from '../common/ExportButton';
import { PERSONA_INFO } from '../../data/mockData';
import { useTheme } from '../../context/ThemeContext';
import { downloadExcel, exportToPDF, getTimestamp } from '../../utils/exportUtils';

// Helper to extract base plan number for grouping
const extractBasePlan = (planNumber) => {
  if (!planNumber) return null;
  const match = planNumber.match(/^([A-Za-z0-9]+)/);
  return match ? match[1] : planNumber;
};

const DashboardView = ({ data, metrics }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Calculate cohort statistics by OC groups
  const cohortStats = useMemo(() => {
    // First, group data by base plan number
    const groups = {};
    data.forEach(client => {
      const basePlan = extractBasePlan(client.id) || client.id;
      if (!groups[basePlan]) {
        groups[basePlan] = {
          basePlan,
          totalRevenue: 0,
          totalWorkload: 0,
          planCount: 0,
          persona: client.persona // Use first plan's persona
        };
      }
      groups[basePlan].totalRevenue += client.revenue;
      groups[basePlan].totalWorkload += client.workloadCost;
      groups[basePlan].planCount += 1;
    });

    // Calculate dominant persona for each group
    const groupList = Object.values(groups).map(g => ({
      ...g,
      margin: g.totalRevenue > 0 ? ((g.totalRevenue - g.totalWorkload) / g.totalRevenue) * 100 : 0,
      profit: g.totalRevenue - g.totalWorkload
    }));

    // Aggregate by cohort/persona
    const stats = {};
    groupList.forEach(group => {
      const persona = group.persona || 'Unknown';
      if (!stats[persona]) {
        stats[persona] = {
          groupCount: 0,
          totalRevenue: 0,
          totalWorkload: 0,
          totalProfit: 0
        };
      }
      stats[persona].groupCount += 1;
      stats[persona].totalRevenue += group.totalRevenue;
      stats[persona].totalWorkload += group.totalWorkload;
      stats[persona].totalProfit += group.profit;
    });

    // Calculate margin for each cohort
    Object.keys(stats).forEach(persona => {
      const s = stats[persona];
      s.margin = s.totalRevenue > 0 ? ((s.totalRevenue - s.totalWorkload) / s.totalRevenue) * 100 : 0;
    });

    return stats;
  }, [data]);
  
  const personaCounts = data.filter(c => !c.isChildPlan).reduce((acc, curr) => {
    acc[curr.persona] = (acc[curr.persona] || 0) + 1;
    return acc;
  }, {});

  const personaData = Object.keys(PERSONA_INFO)
    .filter(key => key !== 'Unknown')
    .map(key => ({
      name: PERSONA_INFO[key].short,
      value: personaCounts[key] || 0,
      fill: PERSONA_INFO[key].color,
  }));

  const mgrVariance = data.reduce((acc, curr) => {
    if (!acc[curr.manager]) acc[curr.manager] = 0;
    acc[curr.manager] += curr.revWorkloadVariance;
    return acc;
  }, {});

  const topManagers = Object.entries(mgrVariance)
    .map(([name, variance]) => ({ name, variance }))
    .sort((a, b) => b.variance - a.variance)
    .slice(0, 5);

  const handleExportExcel = async () => {
    await downloadExcel('clients', `knight_client_data_${getTimestamp()}.xlsx`);
  };

  const handleExportPDF = async () => {
    await exportToPDF('dashboard-content', `knight_dashboard_${getTimestamp()}.pdf`, {
      backgroundColor: isDark ? '#0a0a0b' : '#f8fafc'
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`p-3 rounded-xl border shadow-xl ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{label || payload[0].payload.name}</p>
          <p className="text-xs text-emerald-500 mt-1">
            {typeof payload[0].value === 'number' && payload[0].value > 1000 
              ? `$${payload[0].value.toLocaleString()}` 
              : payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div id="dashboard-content" className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-[10px] p-6 bg-white border border-[#E5E5E5]" style={{boxShadow: 'var(--card-shadow)'}}>
        <div className="relative z-10 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-[#171717] flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-semibold text-[#171717]">AI-Powered Analytics</span>
            </div>
            <h1 className="text-2xl font-bold mb-2 text-[#171717] font-heading tracking-tight">Welcome to TK Analytics</h1>
            <p className="text-sm text-[#525252]">Advanced client analytics and portfolio intelligence. Get actionable insights to optimize your client relationships.</p>
            <LiveIndicator className="mt-3" label="Live Portfolio" />
          </div>
          <ExportButton 
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
            excelLabel="Export Client Data"
            pdfLabel="Export Dashboard PDF"
          />
        </div>
      </div>

      <ExecutiveSummary data={data} metrics={metrics} />

      <DashboardCards metrics={metrics}>
        <CareWatchlistTile />
      </DashboardCards>

      {/* Chart 1: Revenue vs Workload by OC Group (default) */}
      <RevenueWorkloadChart data={data} />
      
      {/* Chart 2: Meeting vs Revenue by OC Group */}
      <MeetingRevenueChart data={data} />
      
      {/* Chart 3: Message Volume vs Revenue by OC Group */}
      <MessageVolumeChart data={data} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        <div className="xl:col-span-2 space-y-6">
          
          <div className="p-6 rounded-[10px] bg-white border border-[#E5E5E5]" style={{boxShadow: 'var(--card-shadow)'}}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[15px] font-bold flex items-center text-[#171717] font-heading">
                <Users className="w-5 h-5 mr-2 text-[#525252]" />
                Client Persona Matrix
              </h3>
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#F5F5F5] text-[#737373]">Live Data</span>
            </div>
            <p className="text-xs mb-6 text-[#737373]">Analyzing Revenue vs. Communication Workload</p>
            <div className="h-[420px]">
               <PersonaChart data={data} />
            </div>
          </div>
          
          <div className="p-6 rounded-[10px] bg-white border border-[#E5E5E5]" style={{boxShadow: 'var(--card-shadow)'}}>
             <h3 className="text-[15px] font-bold flex items-center text-[#171717] font-heading">
                <Info className="w-5 h-5 mr-2 text-[#A3A3A3]" />
                Client Cohort Guide
              </h3>
              <p className="text-[10px] mt-1 mb-4 text-[#A3A3A3]">
                Based on margin health & engagement efficiency
              </p>
              <div className="space-y-3">
                {Object.keys(PERSONA_INFO).filter(p => p !== 'Unknown').map(p => {
                  const stats = cohortStats[p] || { groupCount: 0, totalRevenue: 0, margin: 0 };
                  return (
                  <div key={p} className={`p-3 rounded-xl border transition-colors
                    ${isDark ? 'bg-white/[0.02] border-white/5 hover:border-white/10' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor: PERSONA_INFO[p].color}}></div>
                        <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{p}</h4>
                        {PERSONA_INFO[p].action && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                            PERSONA_INFO[p].action === 'Protect & Replicate' ? 'bg-emerald-500/10 text-emerald-500' :
                            PERSONA_INFO[p].action === 'Maintain & Monitor' ? 'bg-blue-500/10 text-blue-500' :
                            PERSONA_INFO[p].action === 'Growth Focus' ? 'bg-cyan-500/10 text-cyan-500' :
                            PERSONA_INFO[p].action.includes('Immediate') || PERSONA_INFO[p].action.includes('Review') ? 'bg-red-500/10 text-red-500' :
                            'bg-amber-500/10 text-amber-500'
                          }`}>{PERSONA_INFO[p].action}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Cohort Statistics */}
                    <div className={`flex items-center gap-4 mb-2 py-2 px-3 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-white'}`}>
                      <div className="flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-blue-500" />
                        <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.groupCount}</span>
                        <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>groups</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                        <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          ${(stats.totalRevenue / 1000000).toFixed(2)}M
                        </span>
                        <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>revenue</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className={`w-3.5 h-3.5 ${stats.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                        <span className={`text-xs font-bold ${stats.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {stats.margin >= 0 ? '+' : ''}{stats.margin.toFixed(1)}%
                        </span>
                        <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>gross margin</span>
                      </div>
                    </div>
                    
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{PERSONA_INFO[p].description}</p>
                    {PERSONA_INFO[p].recommendation && (
                      <p className={`text-xs mt-1.5 pt-1.5 border-t ${isDark ? 'border-white/5 text-slate-500' : 'border-slate-200 text-slate-400'}`}>
                        <span className="font-medium">→ </span>{PERSONA_INFO[p].recommendation}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="p-6 rounded-[10px] bg-white border border-[#E5E5E5]" style={{boxShadow: 'var(--card-shadow)'}}>
            <h3 className="font-bold mb-6 flex items-center text-[#171717] font-heading">
              <BarChart3 className="w-5 h-5 mr-2 text-[#525252]" /> 
              Cohort Distribution
            </h3>
            <div className="h-[200px] mb-4">
               <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={personaData}>
                   <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                   <XAxis dataKey="name" tick={{fontSize: 10, fill: '#64748b'}} interval={0} angle={-45} textAnchor="end" height={60} axisLine={false} tickLine={false} />
                   <YAxis hide />
                   <Tooltip content={<CustomTooltip />} cursor={{fill: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}} />
                   <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                 </BarChart>
               </ResponsiveContainer>
            </div>
          </div>
          
          <div className="p-6 rounded-[10px] bg-white border border-[#E5E5E5]" style={{boxShadow: 'var(--card-shadow)'}}>
            <h3 className="text-[15px] font-bold mb-6 flex items-center text-[#171717] font-heading">
              <Briefcase className="w-5 h-5 mr-2 text-[#525252]" />
              Top Managers
            </h3>
            <p className="text-xs mb-4 -mt-4 text-[#737373]">By Net Variance</p>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topManagers} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                  <XAxis type="number" tick={{fontSize: 11, fill: '#64748b'}} axisLine={false} tickLine={false} tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 11, fill: isDark ? '#94a3b8' : '#475569', fontWeight: 500}} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{fill: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}} />
                  <Bar dataKey="variance" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
